import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * useInlineButtonEditor — click any button rendered inside the live canvas
 * to open a tiny popover with Label / Link / Color (and optionally Delete).
 *
 * Buttons are tagged with `data-wb-button="1"` and `data-wb-button-index={i}`
 * in `_shared.js` Btn + PremiumSectionRenderer Btn. The owning section block
 * is identified by the nearest `[data-testid^="live-block-"]` ancestor.
 *
 * Callers wire `onUpdateButtons(blockId, nextButtonsArray)` — the hook does
 * the merge/splice for them.
 *
 * Returns:
 *   - `editorState` — { blockId, index, anchorRect } | null
 *   - `closeEditor()` — manually dismiss
 *   - `applyEdit(patch)` — partial { text, link, color } merge
 *   - `removeButton()` — splice the button at `index`
 */
export default function useInlineButtonEditor({ canvasRef, blocks, onUpdateButtons, enabled = true }) {
  const blocksRef = useRef(blocks);
  useEffect(() => { blocksRef.current = blocks; }, [blocks]);
  const [editorState, setEditorState] = useState(null); // { blockId, index, rect }

  const closeEditor = useCallback(() => setEditorState(null), []);

  useEffect(() => {
    if (!enabled) return;

    // Attach to document so we don't depend on canvasRef.current being
    // populated at effect-run time. We still scope by checking the target
    // is inside the canvas node (when provided) so other parts of the page
    // (e.g. master dashboard) are unaffected.
    const onClick = (e) => {
      // Iter 155 — if a fluid drag just ended (within 300ms), this click
      // is the synthetic mouseup→click follow-up. The button editor
      // should NOT open after a successful drag. Bail.
      if (window.__wbDragEndedAt && (Date.now() - window.__wbDragEndedAt) < 300) return;
      const btn = e.target.closest('[data-wb-button="1"]');
      if (!btn) return;
      const canvas = canvasRef?.current;
      if (canvas && !canvas.contains(btn)) return; // outside the canvas
      // Capture: do NOT navigate / inline-edit text inside the button.
      e.preventDefault();
      e.stopPropagation();

      const wrapper = btn.closest('[data-testid^="live-block-"]');
      if (!wrapper) return;
      const testid = wrapper.getAttribute('data-testid') || '';
      const blockId = testid.replace('live-block-', '');
      const indexAttr = btn.getAttribute('data-wb-button-index');
      const index = indexAttr == null ? 0 : parseInt(indexAttr, 10);
      const field = btn.getAttribute('data-wb-button-field') || 'buttons';
      const rect = btn.getBoundingClientRect();
      setEditorState({ blockId, index, field, rect: { top: rect.top, left: rect.left, bottom: rect.bottom, right: rect.right, width: rect.width, height: rect.height } });
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [canvasRef, enabled]);

  // Close on outside click
  useEffect(() => {
    if (!editorState) return;
    const onDoc = (e) => {
      if (e.target.closest('[data-wb-button-editor]')) return;
      if (e.target.closest('[data-wb-button="1"]')) return;
      setEditorState(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [editorState]);

  // Resolve nested path like "_style.buttons" (fall back to flat `content[field]`).
  const getButtonsArr = (block, field) => {
    if (!block) return [];
    if (field && field.includes('.')) {
      const parts = field.split('.');
      let node = block.content;
      for (const p of parts) {
        if (node == null) return [];
        node = node[p];
      }
      return Array.isArray(node) ? node : [];
    }
    return Array.isArray(block?.content?.[field]) ? block.content[field] : [];
  };

  const applyEdit = useCallback((patch) => {
    if (!editorState) return;
    const { blockId, index, field = 'buttons' } = editorState;
    const block = blocksRef.current?.find(b => b.id === blockId);
    if (!block) return;
    const current = getButtonsArr(block, field);

    // Special directive: reorder the button at `index` by `__move` slots.
    if (patch && typeof patch.__move === 'number') {
      const dir = patch.__move;
      const target = index + dir;
      if (target < 0 || target >= current.length) return;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      onUpdateButtons(blockId, next, field);
      // Update editor state to follow the moved button.
      setEditorState(s => s ? { ...s, index: target } : s);
      return;
    }
    // Special directive: duplicate the button at `index` right next to itself.
    if (patch && patch.__duplicate) {
      const src = current[index] || {};
      const clone = { ...src };
      const next = [...current];
      next.splice(index + 1, 0, clone);
      onUpdateButtons(blockId, next, field);
      setEditorState(s => s ? { ...s, index: index + 1 } : s);
      return;
    }

    const next = current.map((b, i) => (i === index ? { ...b, ...patch } : b));
    onUpdateButtons(blockId, next, field);
  }, [editorState, onUpdateButtons]);

  const removeButton = useCallback(() => {
    if (!editorState) return;
    const { blockId, index, field = 'buttons' } = editorState;
    const block = blocksRef.current?.find(b => b.id === blockId);
    if (!block) return;
    const current = getButtonsArr(block, field);
    const next = current.filter((_, i) => i !== index);
    onUpdateButtons(blockId, next, field);
    setEditorState(null);
  }, [editorState, onUpdateButtons]);

  // Resolve the current button object so the popover can prefill inputs.
  const currentButton = (() => {
    if (!editorState) return null;
    const block = blocksRef.current?.find(b => b.id === editorState.blockId);
    if (!block) return null;
    const field = editorState.field || 'buttons';
    const arr = getButtonsArr(block, field);
    return arr[editorState.index] || null;
  })();

  return { editorState, currentButton, closeEditor, applyEdit, removeButton };
}
